import { type FC, useMemo } from 'react';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { UiHStack, UiIconCurrencyCircleDollar, UiStack, UiText, uiStyles } from '@/lib/ui';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';
import { type Order, loadOrder, orderQueryKey } from '@/api/registration';
import { useQuery } from '@tanstack/react-query';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { type ApiResponseSingle } from '@/api/tenantClient';
import QueryContainer from '@/base/QueryContainer/QueryContainer';
import BaseMessageBarError from '@/base/MessageBar/Error';

const OrderReview: FC = () => {
  const { eventId } = useRegisterRoute();
  const { createTenantApiRequest, isLoading: isApiPreparing } = useTenantApi();

  const registrationQuery = useRegistrationQuery(eventId);
  const registration = registrationQuery?.data;
  const orderHash = registration?.orderHash;

  const orderQuery = useQuery<ApiResponseSingle<Order>, Error>({
    queryKey: [orderQueryKey, { orderHash }],
    queryFn: async () => { return await loadOrder(createTenantApiRequest)(orderHash!); },
    enabled: !!orderHash && !!eventId && !isApiPreparing
  });

  return (
    <QueryContainer query={orderQuery} errorMessage="Failed to load purchase data.">
      {(orderData) => {
        const errors = orderData?.errors ?? [];
        if (errors.length > 0) {
          return (
            <UiStack flexGrow={1} paddingBottom={4}>
              {errors.map((error) => {
                return (
                  <BaseMessageBarError key={error} mt={3}>{error}</BaseMessageBarError>
                );
              })}
            </UiStack>
          );
        }
        return (
          <UiStack>
            <UiHStack justifyContent={'space-between'}>
              <UiText variant={'title'}>
                Your purchase
              </UiText>
            </UiHStack>
            <UiStack
              p={8}
              bgColor={'#fff'}
              borderRadius={uiStyles.borderRadius}
            >
              {orderData?.item?.orderItems?.map((item, index) => {
                return (
                  <UiStack key={index}>
                    <UiText>{item.name}</UiText>
                    <UiHStack justifyContent={'flex-start'}>
                      <UiIconCurrencyCircleDollar size={'xl'} color={'text.primary'} />
                      <UiText variant={'body2'}>Price</UiText>
                      <UiText variant={'body2'}>{item.priceFormat}</UiText>
                    </UiHStack>
                  </UiStack>
                );
              })}
            </UiStack>
          </UiStack>
        );
      }}
    </QueryContainer>
  );
};

export default OrderReview;
